import React from 'react';

import produce from 'immer';
import ms from 'ms';
import { gql } from '@soundxyz/gql-string';
import {
  ERROR_TYPE,
  FEATURES,
  MISCELLANEOUS_ERROR_ACTIONS,
} from '@soundxyz/utils/constants/logging';
import { SpotifyConnectionSuccessModal } from '@components/modals/Spotify/SpotifyConnectionSuccessModal';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalType, useModal } from '@context/modal';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  PresaveSpotifyReleaseDocument,
  ReleasePageDocument,
  ReleasesArePresavedDocument,
  SpotifyConnectionModalReleaseDocument,
  useMutation,
  useQuery,
} from '@graphql/reactQuery';
import { useIsReleasePresaved } from '@hooks/useIsReleasePresaved';
import { logError } from '@hooks/useLogErrorNew';
import { SpotifyConnectState, useSpotifyAuth } from '@hooks/useSpotifyAuth';
import { useStableCallback } from '@hooks/useStableCallback';
import { DefaultVariant } from './variants/Default';
import { HeroVariant, MobileHeroVariant } from './variants/Hero';
import { MintPanelVariant } from './variants/MintPanel';
import { NotificationVariant } from './variants/Notification';
import { SoundBarVariant } from './variants/SoundBar';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation PresaveSpotifyRelease($releaseId: UUID!) {
    presaveRelease(input: { releaseId: $releaseId }) {
      __typename
      ... on Error {
        message
      }
    }
  }
`);

type Props = {
  releaseId: string;
  variant: 'default' | 'mint-component' | 'sound-bar' | 'mobile-hero' | 'hero' | 'notification';
};

RefetchOnComplete({
  trigger: [PresaveSpotifyReleaseDocument],
  refetch: [ReleasesArePresavedDocument, ReleasePageDocument],
});

export function SpotifyPresaveButton({ releaseId, variant }: Props) {
  const spotifyAuth = useSpotifyAuth();
  const { login } = useConnectedAuth();

  const { openModal } = useModal();

  const { isReleasePresaved, setQueryData } = useIsReleasePresaved({ releaseId });
  const { mutateAsync: presaveMutate, isLoading: isPresaveLoading } = useMutation(
    PresaveSpotifyReleaseDocument,
    {},
  );

  const { data: spotifyModalReleaseData } = useQuery(SpotifyConnectionModalReleaseDocument, {
    staleTime: 0,
    variables: {
      id: releaseId,
    },
  });

  const loading = spotifyAuth.type === 'loading' || isPresaveLoading;

  const presave = useStableCallback(async () => {
    try {
      const result = await presaveMutate({ releaseId });

      if (result.data.presaveRelease.__typename === 'MutationPresaveReleaseSuccess') {
        setQueryData(data => {
          if (!data) {
            return {
              data: {
                userReleaseStatus: [
                  {
                    releaseId,
                    isPreSaved: true,
                  },
                ],
              },
            };
          }

          return produce(data, draft => {
            for (const value of draft.data.userReleaseStatus) {
              if (value.releaseId === releaseId) {
                value.isPreSaved = true;
                break;
              }
            }
          });
        });

        if (spotifyModalReleaseData?.data.release?.id) {
          openModal(ModalType.SPOTIFY_CONNECTION_SUCCESS, {
            body: (
              <SpotifyConnectionSuccessModal releaseFrag={spotifyModalReleaseData.data.release} />
            ),
          });
        }
      } else {
        logError({
          action: MISCELLANEOUS_ERROR_ACTIONS.SPOTIFY_PRESAVE_ERROR,
          errorType: ERROR_TYPE.SPOTIFY_PRESAVE,
          isArtistOnlyPage: false,
          level: 'error',
          feature: FEATURES.SPOTIFY_PRESAVE,
          error: new Error(result.data.presaveRelease.message),
          message: result.data.presaveRelease.message,
          toast: 'There was an error presaving this release. Please try again.',
        });
      }
    } catch (error) {
      const message = 'Error presaving Spotify release';
      logError({
        action: MISCELLANEOUS_ERROR_ACTIONS.SPOTIFY_PRESAVE_ERROR,
        errorType: ERROR_TYPE.SPOTIFY_PRESAVE,
        isArtistOnlyPage: false,
        level: 'error',
        feature: FEATURES.SPOTIFY_PRESAVE,
        error,
        message,
        toast: 'There was an error presaving this release. Please try again.',
      });
    }
  });

  const onClick = useStableCallback(async () => {
    if (isReleasePresaved) {
      return;
    }

    switch (spotifyAuth.type) {
      case 'spotify-auth':
        const cleanup = SpotifyConnectState.onTabSync(() => {
          if (SpotifyConnectState.state.value?.justConnected) {
            presave();
            cleanup();
            clearTimeout(cleanupTimeout);
          }
        });

        const cleanupTimeout = setTimeout(() => {
          cleanup();
        }, ms('2 minutes'));
        break;
      case 'not-authenticated':
        login();
        break;
      case 'already-connected':
        presave();
    }
  });

  switch (variant) {
    case 'sound-bar':
      return (
        <SoundBarVariant
          isLoading={loading}
          isSaved={isReleasePresaved}
          onClick={onClick}
          spotifyLink={spotifyAuth.link}
        />
      );
    case 'hero':
      return (
        <HeroVariant
          isLoading={loading}
          isSaved={isReleasePresaved}
          onClick={onClick}
          spotifyLink={spotifyAuth.link}
        />
      );
    case 'mobile-hero':
      return (
        <MobileHeroVariant
          isLoading={loading}
          isSaved={isReleasePresaved}
          onClick={onClick}
          spotifyLink={spotifyAuth.link}
        />
      );
    case 'mint-component':
      return (
        <MintPanelVariant
          isLoading={loading}
          isSaved={isReleasePresaved}
          onClick={onClick}
          spotifyLink={spotifyAuth.link}
        />
      );
    case 'notification':
      return (
        <NotificationVariant
          isSaved={isReleasePresaved}
          onClick={onClick}
          spotifyLink={spotifyAuth.link}
        />
      );
    case 'default':
    default:
      return (
        <DefaultVariant
          isLoading={loading}
          isSaved={isReleasePresaved}
          onClick={onClick}
          spotifyLink={spotifyAuth.link}
        />
      );
  }
}
